import { useRef, useState } from "react";
import { UploadCloud, FileText, ImageIcon, Loader2, X } from "lucide-react";
import { api, getAxiosErrorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function FileUpload({ entityType, entityId, onUploaded, compact = false }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (onUploaded) onUploaded(data);
      }
      toast.success("Fichier(s) ajouté(s)");
    } catch (e) {
      toast.error(getAxiosErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      data-testid="file-upload-zone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-2xl text-center cursor-pointer transition-colors group
        ${dragOver ? "border-blue-500 bg-blue-50/80" : "border-blue-200 bg-blue-50/30 hover:bg-blue-50/80"}
        ${compact ? "p-4" : "p-8"}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        data-testid="file-upload-input"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {uploading ? (
        <div className="flex items-center justify-center gap-2 text-blue-700">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
          <span className="text-sm">Envoi en cours…</span>
        </div>
      ) : (
        <>
          <UploadCloud
            className={`mx-auto h-10 w-10 text-blue-400 group-hover:text-blue-600 transition-colors ${compact ? "h-6 w-6" : ""}`}
            strokeWidth={1.5}
          />
          <p className="mt-2 text-sm font-medium text-slate-700">
            Glissez des fichiers ici ou cliquez pour parcourir
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Photos (JPG, PNG), PDF, documents jusqu'à 15 MB
          </p>
        </>
      )}
    </div>
  );
}

export function FileList({ files = [], onDelete, getDownloadUrl }) {
  if (!files.length) return null;
  return (
    <div className="space-y-2">
      {files.map((f) => {
        const isImage = (f.content_type || "").startsWith("image/");
        return (
          <div
            key={f.id}
            data-testid={`file-item-${f.id}`}
            className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:border-blue-200 transition-colors"
          >
            <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
              {isImage ? <ImageIcon size={16} /> : <FileText size={16} />}
            </div>
            <div className="flex-1 min-w-0">
              <a
                href={getDownloadUrl(f.id)}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-slate-900 hover:text-blue-700 truncate block"
              >
                {f.original_filename}
              </a>
              <div className="text-xs text-slate-500">
                {(f.size / 1024).toFixed(1)} Ko
              </div>
            </div>
            {onDelete ? (
              <button
                onClick={() => onDelete(f.id)}
                className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                data-testid={`file-delete-${f.id}`}
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
